import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { CourseEditBasic } from "@shared/models/analytics/course"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import { partial } from "lodash-es"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultCourseActivity from "./default-course-activity"
import defaultCourseContext from "./default-course-context"
import defaultCourseModels from "./default-course-models"

interface Payload {
  courseId: string
  planbookId: string
  doNotApplyTemplatesAutomatically: boolean
}

export function prepareCourseSetDoNotApplyTemplatesAutomatically(payload: Payload): PreparedAction {
  return {
    name: COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY.name,
    payload: payload,
    patches: COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY.patches(payload),
    undoPatches: COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY.undoPatches(payload),
    narrative: partial(COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY.narrative, payload),
  }
}

export const COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY = {
  name: "COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY",

  patches(payload: Payload): Array<PatchModel> {
    return [
      Patch.create("course", payload.courseId).set(
        "attributes.doNotApplyTemplatesAutomatically",
        payload.doNotApplyTemplatesAutomatically
      ).patch,
    ]
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return [
      Patch.create("course", payload.courseId).set(
        "attributes.doNotApplyTemplatesAutomatically",
        !payload.doNotApplyTemplatesAutomatically
      ).patch,
    ]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultCourseModels(payload, findFn, userId)
    let courseTitle = models.course?.attributes.title
    let newEvent: CourseEditBasic = {
      ...createBlankEvent(),
      root_id: payload.planbookId,
      root_type: "planbook",
      date_plan_scheduled_for: null,

      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbookId),

      primary_object_type: "course",
      primary_object_id: payload.courseId,
      primary_object_specifier: "automatic_templates",
      primary_object_id_and_ancestor_ids: null,

      adverbial_verb: null,

      adverbial_object_type: null,
      adverbial_object_id: null,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: null,
    }

    return {
      title: payload.doNotApplyTemplatesAutomatically
        ? "Turned on template"
        : "Turned off template",
      titleForAnalytics: "Course - Set Automatically Apply Template",
      description: payload.doNotApplyTemplatesAutomatically
        ? `Turned on the automatic template for your "${courseTitle}" class`
        : `Turned off the automatic template for your "${courseTitle}" class`,
      context: defaultCourseContext(payload, models),
      activity: defaultCourseActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default COURSE_SET_DO_NOT_APPLY_TEMPLATES_AUTOMATICALLY
